// Sprint 12.5 ACC.C1 (#421) — Fast Track project wizard.
//
// Dedicated single-step modal distinct from the standard NewProjectModal:
// the Fast Track creation contract is small enough (title + description
// + urgency label) that wizardising it in steps would be ceremony for
// its own sake. The single-step shape also matches the issue's spec:
// "Fast Track wizard: single step — issue title + description + label".
//
// On submit:
//   POST /projects { projectType: 'fast_track', name, fastTrack: { title, description, label } }
//
// The backend (routes/projects.ts) treats this as the trust-boundary
// signal and:
//   * sets currentPhase = phase_3_sprint_planning
//   * pre-creates a single issue with the supplied label
//   * skips the Bootstrap Agent (which scaffolds Phase 1 artefacts)
//
// On success the modal navigates to the new project's chat view, where
// Sprint Planning will open with the Fast Track banner from
// agentRegistry.ts's promptBuilder fast-track branch.

import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { HelpPopover } from './help';
import GithubIssueImportSection, { type GithubSource } from './GithubIssueImportSection';
import type { MappedFastTrackFields } from '../lib/githubIssueImport';

interface FastTrackWizardModalProps {
  onClose:   () => void;
  onSuccess: () => void;
}

type FastTrackLabel = 'bug' | 'hotfix' | 'security';

const LABEL_OPTIONS: Array<{ value: FastTrackLabel; description: string }> = [
  { value: 'bug',      description: 'Defect in shipped behaviour. Routine fix priority.' },
  { value: 'hotfix',   description: 'Time-sensitive fix needed in production.' },
  { value: 'security', description: 'Vulnerability or security regression. Highest urgency.' },
];

export default function FastTrackWizardModal({ onClose, onSuccess }: FastTrackWizardModalProps) {
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState('');
  const [issueTitle, setIssueTitle] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [label, setLabel] = useState<FastTrackLabel>('bug');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // FT-IMPORT Story 2 — provenance of an imported GitHub issue. null = manual
  // (today's flow); set only when the optional picker applies a selection.
  const [githubSource, setGithubSource] = useState<GithubSource | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmedName = projectName.trim();
    const trimmedTitle = issueTitle.trim();
    const trimmedDesc = issueDescription.trim();
    if (!trimmedName || !trimmedTitle || !trimmedDesc) return;

    setLoading(true);
    setError('');

    try {
      const created = await apiFetch<{ id: string }>('/projects', {
        method: 'POST',
        body: JSON.stringify({
          name:        trimmedName,
          projectType: 'fast_track',
          // FT-IMPORT Story 2 — carry githubSource ONLY when an issue was
          // imported; omitting it keeps the manual create body byte-identical.
          fastTrack:   { title: trimmedTitle, description: trimmedDesc, label, ...(githubSource ? { githubSource } : {}) },
        }),
      });
      onSuccess();
      navigate(`/projects/${created.id}/chat`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create Fast Track project');
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)', zIndex: 50 }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[85vh] flex flex-col"
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-lg)',
        }}
        onClick={e => e.stopPropagation()}
        data-testid="fast-track-wizard"
      >
        {/* Header — pinned (matches NewProjectModal's standard modal shell) */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
              New Fast Track project
            </h2>
            <HelpPopover
              id="fast-track-wizard-overview"
              variant="warning"
              heading="What FastTrack skips"
              body={
                <>
                  <p style={{ margin: '0 0 8px' }}>
                    FastTrack skips Phase 1 (Requirements) and Phase 2 (Architecture). Your project starts directly at Phase 3 (Sprint Planning) using your inputs as the agent&apos;s interpretation of scope and architecture.
                  </p>
                  <p style={{ margin: '0 0 8px' }}>
                    This is faster but means no human review of the requirements or architecture decisions before development begins. Errors in scope or architecture surface in Phase 4 or later, when correcting them is more expensive.
                  </p>
                  <p style={{ margin: 0 }}>
                    Use FastTrack when you have high confidence in your scope, you&apos;re rebuilding something familiar, or you&apos;ve already done the requirements and architecture work outside BluePrint. If you&apos;re unsure, the standard project flow gives you Phase 1 and 2 review and is only a few sprints slower.
                  </p>
                </>
              }
            />
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 18 }}>×</button>
        </div>

        {/* Form: scrollable body + pinned footer — the standard modal shell */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5">
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
            Fast Track skips Phases 1 (Requirements) and 2 (Architecture) and starts at Phase 3 (Sprint Planning).
            A single issue is pre-created — additional issues can&apos;t be added later.
            For multi-scope work, use the standard New Project flow instead.
          </p>

          {error && (
            <div data-testid="fast-track-error" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 12, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)' }}>
              {error}
            </div>
          )}

          <Field label="Project name" htmlFor="ft-name">
            <input
              id="ft-name"
              type="text"
              required
              value={projectName}
              onChange={e => setProjectName(e.target.value)}
              maxLength={100}
              data-testid="fast-track-name"
              style={inputStyle}
            />
          </Field>

          <Field label="Issue title" htmlFor="ft-title">
            <input
              id="ft-title"
              type="text"
              required
              value={issueTitle}
              onChange={e => setIssueTitle(e.target.value)}
              maxLength={200}
              data-testid="fast-track-issue-title"
              style={inputStyle}
            />
          </Field>

          <Field label="Issue description" htmlFor="ft-description">
            <textarea
              id="ft-description"
              required
              value={issueDescription}
              onChange={e => setIssueDescription(e.target.value)}
              maxLength={5000}
              rows={5}
              data-testid="fast-track-issue-description"
              style={{ ...inputStyle, height: 'auto', minHeight: 100, padding: 10, resize: 'vertical' }}
            />
          </Field>

          <Field label="Label" htmlFor="ft-label">
            <select
              id="ft-label"
              value={label}
              onChange={e => setLabel(e.target.value as FastTrackLabel)}
              data-testid="fast-track-label"
              style={inputStyle}
            >
              {LABEL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.value}</option>
              ))}
            </select>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              {LABEL_OPTIONS.find(opt => opt.value === label)?.description}
            </p>
          </Field>

          <GithubIssueImportSection
            onApply={(fields: MappedFastTrackFields, source: GithubSource) => {
              setIssueTitle(fields.title);
              setIssueDescription(fields.description);
              if (fields.label) setLabel(fields.label);
              setGithubSource(source);
              // Convenience: seed an empty project name from the issue title
              // (still editable). Sliced to the name field's 100-char cap.
              setProjectName(prev => (prev.trim() ? prev : fields.title.slice(0, 100)));
            }}
            onClear={() => setGithubSource(null)}
          />
          </div>

          {/* Footer — pinned, always visible */}
          <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4" style={{ borderTop: '1px solid var(--border)' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{ ...buttonStyle, background: 'transparent', color: 'var(--text)', border: '1px solid var(--border)' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !projectName.trim() || !issueTitle.trim() || !issueDescription.trim()}
              data-testid="fast-track-submit"
              style={{ ...buttonStyle, background: 'var(--primary)', color: 'white', border: 'none', opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Creating…' : 'Create Fast Track project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  display:      'block',
  width:        '100%',
  height:       40,
  background:   'var(--surface-alt)',
  border:       '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding:      '0 12px',
  fontFamily:   'var(--font-body)',
  fontSize:     14,
  color:        'var(--text)',
  outline:      'none',
  boxSizing:    'border-box',
};

const buttonStyle: React.CSSProperties = {
  height:       38,
  padding:      '0 16px',
  fontFamily:   'var(--font-display)',
  fontSize:     14,
  fontWeight:   600,
  borderRadius: 'var(--radius-sm)',
  cursor:       'pointer',
};

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={htmlFor} style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
